import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { CalendarDays, ChevronDown, Download, FileText, Search } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { formatDate } from '../lib/dates';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateToYMD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getLast8WeeklyRanges = () => {
  const weeks = [];
  const currentDate = new Date();
  const dayOfTheWeek = currentDate.getDay();
  const diff = currentDate.getDate() - (dayOfTheWeek + 1);

  for (let i = 0; i < 8; i += 1) {
    // se crea una instancia nueva de current date para evitar mutar la fecha actual
    const weekStart = new Date(currentDate);
    weekStart.setDate(diff - (i * 7));

    // se crea la fecha de fin a partir de la fecha en que inicia la semana
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    const weekStartYMD = formatDateToYMD(weekStart);
    const weekEndYMD = formatDateToYMD(weekEnd);

    weeks.push({
      value: weekStartYMD,
      start: weekStartYMD,
      end: weekEndYMD,
      label: `${formatDate(weekStartYMD)} a ${formatDate(weekEndYMD)}`,
    });
  }

  return weeks;
};

const getLast6MonthlyOptions = () => {
  const today = new Date();
  const months = [];

  for (let i = 0; i < 6; i += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const value = `${year}-${month}`;

    const label = date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
    });

    months.push({
      value,
      label: label.charAt(0).toUpperCase() + label.slice(1),
    });
  }

  return months;
};


export const MisActividades = () => {
  const navigate = useNavigate();
  const { token, canEdit, loading: authLoading } = useAuth();

  const [activities, setActivities] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [reportType, setReportType] = useState('weekly');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedWeeklyReferenceDate, setSelectedWeeklyReferenceDate] = useState('');
  const [selectedReportMonth, setSelectedReportMonth] = useState('');
  const [search, setSearch] = useState('');
  const [territoryFilter, setTerritoryFilter] = useState('all');

  const weeklyOptions = useMemo(() => getLast8WeeklyRanges(), []);
  const monthlyOptions = useMemo(() => getLast6MonthlyOptions(), []);

  const fetchMyActivities = useCallback(async () => {
    try {
      setLoading(true);

      const headers = { Authorization: `Bearer ${token}` };

      const [meRes, activitiesRes, dependenciesRes, territoriesRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers }),
        axios.get(`${API}/activities?status=published`, { headers }),
        axios.get(`${API}/dependencies`, { headers }),
        axios.get(`${API}/territories`, { headers }),
      ]);

      const me = meRes.data || null;
      const allActivities = Array.isArray(activitiesRes.data) ? activitiesRes.data : [];
      const dependenciesData = Array.isArray(dependenciesRes.data) ? dependenciesRes.data : [];
      const territoriesData = Array.isArray(territoriesRes.data) ? territoriesRes.data : [];


      const mine = allActivities.filter(
        (activity) => String(activity.created_by) === String(me?.id)
      );

      setActivities(mine);
      setDependencies(dependenciesData);
      setTerritories(territoriesData);
    } catch (error) {
      console.error(error);
      toast.error('No fue posible cargar tus actividades');
      setActivities([]);
      setDependencies([]);
      setTerritories([]);
    } finally {
      setLoading(false);
    }
  }, [token]);



  useEffect(() => {
    if (weeklyOptions.length > 0 && !selectedWeeklyReferenceDate) {
      setSelectedWeeklyReferenceDate(weeklyOptions[0].value);
    }
  }, [weeklyOptions, selectedWeeklyReferenceDate]);

  useEffect(() => {
    if (monthlyOptions.length > 0 && !selectedReportMonth) {
      setSelectedReportMonth(monthlyOptions[0].value);
    }
  }, [monthlyOptions, selectedReportMonth]);




  useEffect(() => {
    if (authLoading) return;

    if (!canEdit) {
      navigate('/');
      return;
    }

    if (!token) {
      setLoading(false);
      return;
    }

    fetchMyActivities();
  }, [authLoading, canEdit, navigate, token, fetchMyActivities]);

  const getDependencyName = useCallback(
    (activity) => {
      return (
        activity.dependency_name ||
        dependencies.find((item) => String(item.id) === String(activity.dependency_id))?.name ||
        '—'
      );
    },
    [dependencies]
  );

  const getTerritoryName = useCallback(
    (activity) => {
      return (
        activity.territory_name ||
        territories.find((item) => String(item.id) === String(activity.territory_id))?.name ||
        '—'
      );
    },
    [territories]
  );

  const territoryOptions = useMemo(() => {
    return [
      ...new Set(
        activities
          .map((activity) => getTerritoryName(activity))
          .filter((name) => name && name !== '—')
      ),
    ].sort((a, b) => a.localeCompare(b, 'es'));
  }, [activities, getTerritoryName]);

  const filtered = useMemo(() => {
    return activities.filter((activity) => {
      const territoryName = getTerritoryName(activity);
      const dependencyName = getDependencyName(activity);

      const haystack =
        `${activity.title || ''} ${territoryName} ${dependencyName}`.toLowerCase();

      const matchesSearch = !search || haystack.includes(search.toLowerCase());
      const matchesTerritory =
        territoryFilter === 'all' || territoryName === territoryFilter;

      return matchesSearch && matchesTerritory;
    });
  }, [activities, search, territoryFilter, getDependencyName, getTerritoryName]);

  const getReportFileName = (contentDisposition, fallbackName) => {
    if (!contentDisposition) return fallbackName;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].replace(/["]/g, ''));
    }

    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (filenameMatch?.[1]) {
      return filenameMatch[1];
    }

    return fallbackName;
  };


  const getMonthDateRange = (monthValue) => {
    if (!monthValue) return null;

    const [yearStr, monthStr] = monthValue.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);

    if (!year || !month) return null;

    const startDate = `${yearStr}-${monthStr}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${yearStr}-${monthStr}-${String(lastDay).padStart(2, '0')}`;

    return { startDate, endDate };
  };

  const handleDownloadMonthlyReport = async () => {
    try {
      if (!selectedReportMonth) {
        toast.error('Selecciona un mes');
        return;
      }

      const range = getMonthDateRange(selectedReportMonth);

      if (!range) {
        toast.error('Mes inválido');
        return;
      }

      setDownloadingReport(true);

      const response = await axios.get(`${API}/reports/monthly/export/me`, {
        params: {
          start_date: range.startDate,
          end_date: range.endDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const contentDisposition = response.headers?.['content-disposition'];
      const fileName = getReportFileName(
        contentDisposition,
        `reporte_mensual_mi_dependencia_${range.startDate}_${range.endDate}.xlsx`
      );

      const blob = new Blob([response.data], {
        type:
          response.headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Reporte mensual descargado correctamente');
      setReportDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('No fue posible descargar el reporte mensual');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDownloadWeeklyReport = async () => {
    try {
      if (!selectedWeeklyReferenceDate) {
        toast.error('Selecciona una semana');
        return;
      }

      setDownloadingReport(true);

      const response = await axios.get(`${API}/reports/weekly/export/me`, {
        params: {
          reference_date: selectedWeeklyReferenceDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const contentDisposition = response.headers?.['content-disposition'];
      const fileName = getReportFileName(
        contentDisposition,
        `reporte_semanal_mi_dependencia_${selectedWeeklyReferenceDate}.xlsx`
      );

      const blob = new Blob([response.data], {
        type:
          response.headers?.['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Reporte semanal descargado correctamente');
      setReportDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('No fue posible descargar el reporte semanal');
    } finally {
      setDownloadingReport(false);
    }
  };

  if (!canEdit) return null;

  return (
    <div className="min-h-screen bg-[#F5F0E1] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="border border-[#D4C5A5]/30">
          <CardHeader className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#8B1C4A]" />
                Ver mis actividades ({activities.length})
              </CardTitle>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="border-[#8B1C4A] text-[#8B1C4A] hover:bg-[#8B1C4A]/5 hover:text-[#8B1C4A] active:text-[#8B1C4A] w-full sm:w-auto"
                  onClick={() => setReportDialogOpen(true)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Reporte
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>

                <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                  <DialogContent className="w-[calc(100vw-32px)] max-w-[560px] max-h-[90vh] overflow-y-auto p-0">
                    <DialogHeader className="px-5 pt-5 pb-2 sm:px-6 pr-12">
                      <DialogTitle className="text-center sm:text-left">
                        Descargar reporte
                      </DialogTitle>
                      <DialogDescription className="text-center sm:text-left">
                        Selecciona si deseas descargar el reporte semanal o mensual de tu dependencia.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5 px-5 pb-5 sm:px-6">
                      <div className="space-y-2">
                        <Label>Tipo de reporte</Label>
                        <Select value={reportType} onValueChange={setReportType}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona una opción" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Reporte semanal</SelectItem>
                            <SelectItem value="monthly">Reporte mensual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {reportType === 'weekly' ? (
                        <div className="space-y-2">
                          <Label>Semana a exportar</Label>
                          <Select
                            value={selectedWeeklyReferenceDate}
                            onValueChange={setSelectedWeeklyReferenceDate}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona una semana" />
                            </SelectTrigger>
                            <SelectContent>
                              {weeklyOptions.map((week) => (
                                <SelectItem key={week.value} value={week.value}>
                                  {week.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500">
                            Se muestran las últimas 8 semanas en formato sábado a sábado.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label>Mes a exportar</Label>
                          <Select
                            value={selectedReportMonth}
                            onValueChange={setSelectedReportMonth}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona un mes" />
                            </SelectTrigger>
                            <SelectContent>
                              {monthlyOptions.map((month) => (
                                <SelectItem key={month.value} value={month.value}>
                                  {month.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500">
                            Se muestran los últimos 6 meses disponibles para descarga.
                          </p>
                        </div>
                      )}

                      <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setReportDialogOpen(false)}
                          className="w-full sm:w-auto"
                          disabled={downloadingReport}
                        >
                          Cancelar
                        </Button>

                        <Button
                          type="button"
                          className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                          disabled={
                            downloadingReport ||
                            (reportType === 'weekly' && !selectedWeeklyReferenceDate) ||
                            (reportType === 'monthly' && !selectedReportMonth)
                          }
                          onClick={
                            reportType === 'weekly'
                              ? handleDownloadWeeklyReport
                              : handleDownloadMonthlyReport
                          }
                        >
                          <Download className="w-4 h-4 mr-2" />
                          {downloadingReport ? 'Descargando...' : 'Descargar Excel'}
                        </Button>
                      </DialogFooter>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                  onClick={() => navigate('/cargar-actividad')}
                >
                  Registrar actividad
                </Button>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por título, territorio o dependencia..."
                  className="pl-10"
                />
              </div>

              <div className="max-w-md">
                <Select value={territoryFilter} onValueChange={setTerritoryFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filtrar por territorio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los territorios</SelectItem>
                    {territoryOptions.map((territory) => (
                      <SelectItem key={territory} value={territory}>
                        {territory}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-slate-500">Cargando actividades...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                No se encontraron actividades.
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((activity) => {
                  const title = activity.title || 'Actividad sin título';
                  const dependencyName = getDependencyName(activity);
                  const territoryName = getTerritoryName(activity);
                  const activityDate =
                    formatDate(
                      activity.start_date ||
                        activity.week_start ||
                        activity.created_at?.slice(0, 10)
                    ) || '—';

                  return (
                    <div
                      key={activity.id}
                      className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <button
                          type="button"
                          className="text-left"
                          onClick={() => navigate(`/actividad/${activity.id}`)}
                        >
                          <p className="font-semibold text-slate-800 hover:text-[#8B1C4A] transition-colors">
                            {title}
                          </p>
                        </button>

                        <div className="mt-2 space-y-1 text-sm text-slate-600">
                          <p>
                            <span className="font-medium">Dependencia:</span> {dependencyName}
                          </p>
                          <p>
                            <span className="font-medium">Territorio:</span> {territoryName}
                          </p>
                          <p className="flex items-center gap-2">
                            <CalendarDays className="w-4 h-4" />
                            {activityDate}
                          </p>

                          {activity.editable_until && (
                            <p>
                              <span className="font-medium">Editable hasta:</span>{' '}
                              {activity.editable_until}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-green-100 text-green-800">
                          Publicada
                        </Badge>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/actividad/${activity.id}`)}
                        >
                          Ver
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MisActividades;